import axios, { AxiosInstance } from 'axios';
import { NetworkSummary, NodeDetails, NodePosition, NodeConnection, Message, TelemetryDataPoint, Packet } from '../types/api';

// Base API URL - using only the static URL
const API_BASE_URL = 'https://backend-nhmesh.vercel.app/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for all API calls
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance with longer timeout for node details
const nodeDetailsApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 45000, // 45 seconds for node details
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add simple retry logic to both API instances
api.interceptors.response.use(null, async (error: any) => {
  // Only retry on timeout or 5xx errors
  if (error.code === 'ECONNABORTED' || 
      (error.response && error.response.status >= 500 && error.response.status < 600)) {
    
    const config = error.config;
    
    // Set retry count
    config.retryCount = config.retryCount || 0;
    
    // Only retry once (2 total attempts) to fail faster
    if (config.retryCount < 1) {
      config.retryCount += 1;
      console.log(`Retrying API request (${config.retryCount}/1)...`);
      
      // Delay before retry (shorter delay)
      const delay = 500;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return api(config);
    }
  }
  
  return Promise.reject(error);
});

// Add the same retry logic to nodeDetailsApi
nodeDetailsApi.interceptors.response.use(null, async (error: any) => {
  // Only retry on timeout or 5xx errors
  if (error.code === 'ECONNABORTED' || 
      (error.response && error.response.status >= 500 && error.response.status < 600)) {
    
    const config = error.config;
    
    // Set retry count
    config.retryCount = config.retryCount || 0;
    
    // Only retry once (2 total attempts) to fail faster
    if (config.retryCount < 1) {
      config.retryCount += 1;
      console.log(`Retrying node details API request (${config.retryCount}/1)...`);
      
      // Delay before retry (shorter delay)
      const delay = 500;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return nodeDetailsApi(config);
    }
  }
  
  return Promise.reject(error);
});

/**
 * Fetch network summary data
 * @returns Promise with network summary data
 */
export const fetchNetworkSummary = async (): Promise<NetworkSummary> => {
  try {
    const response = await api.get('/network/summary');
    return response.data;
  } catch (error) {
    console.error('Error fetching network summary:', error);
    throw error;
  }
};

/**
 * Fetch node positions for the map
 * @returns Promise with node positions data
 */
export const fetchNodePositions = async (): Promise<NodePosition[]> => {
  try {
    // Match the existing API endpoint structure
    const response = await api.get('/positions');
    
    console.log('Node positions response:', response.data);
    
    // Transform the response to match our frontend data structure
    return response.data.map((position: any) => {
      // Ensure latitude and longitude are valid numbers
      const latitude = parseFloat(position.latitude);
      const longitude = parseFloat(position.longitude);
      
      return {
        nodeId: parseInt(position.node_id, 10),
        latitude: isFinite(latitude) ? latitude : null,
        longitude: isFinite(longitude) ? longitude : null,
        altitude: position.altitude || 0,
        isGateway: position.is_gw === true,
        // Ensure is_online is properly interpreted as a boolean
        isOnline: position.is_online === true,
        // Use the battery level from the API or default to 75
        batteryLevel: position.battery_level || 75,
      };
    });
  } catch (error) {
    console.error('Error fetching node positions:', error);
    throw error;
  }
};

/**
 * Fetch node connections for the map
 * @returns Promise with node connections data
 */
export const fetchNodeConnections = async (): Promise<NodeConnection[]> => {
  try {
    // Get self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
      
    // Try to fetch from the mesh-network-view endpoint first
    try {
      const response = await api.get('/mesh-network-view');
      
      // Transform the response to match our frontend data structure
      let connections = response.data.map((connection: any) => ({
        sourceId: parseInt(connection.source_id, 10),
        targetId: parseInt(connection.destination_id, 10),
        viaNodeId: connection.via_node_id ? parseInt(connection.via_node_id, 10) : null,
        quality: connection.quality || (connection.snr ? Math.min(100, Math.max(0, connection.snr * 10)) : 50),
        hopCount: connection.hop_count || 1,
        connectionType: connection.connection_type || 'direct',
        isHistorical: false
      }));
      
      // Filter out connections involving the self/home node
      if (selfNodeId) {
        connections = connections.filter((connection: NodeConnection) => 
          connection.sourceId !== selfNodeId && connection.targetId !== selfNodeId
        );
      }
      
      return connections;
    } catch (networkViewError) {
      console.warn('Error fetching from mesh-network-view, falling back to neighbors endpoint:', networkViewError);
      
      // Fall back to the original endpoint
      const response = await api.get('/neighbors');
      
      // Transform the response to match our frontend data structure
      let connections = response.data.map((connection: any) => ({
        sourceId: parseInt(connection.node_id, 10),
        targetId: parseInt(connection.neighbor_id, 10),
        viaNodeId: null,
        quality: connection.quality || (connection.snr ? Math.min(100, Math.max(0, connection.snr * 10)) : 50),
        hopCount: connection.hop_count || 1,
        connectionType: connection.connection_type || 'direct',
        isHistorical: false
      }));
      
      // Filter out connections involving the self/home node
      if (selfNodeId) {
        connections = connections.filter((connection: NodeConnection) => 
          connection.sourceId !== selfNodeId && connection.targetId !== selfNodeId
        );
      }
      
      return connections;
    }
  } catch (error) {
    console.error('Error fetching node connections:', error);
    throw error;
  }
};

/**
 * Fetch multi-hop mesh routes
 * @param hours Number of hours to look back (default: 24)
 * @returns Promise with mesh route data
 */
export const fetchMeshRoutes = async (hours: number = 24): Promise<NodeConnection[]> => {
  try {
    const response = await api.get('/mesh-routes', {
      params: { hours }
    });
    
    // Transform the response to match our frontend data structure
    return response.data.map((route: any) => ({
      sourceId: parseInt(route.source_id, 10),
      targetId: parseInt(route.destination_id, 10),
      viaNodeId: route.via_node_id ? parseInt(route.via_node_id, 10) : null,
      quality: route.quality || 50,
      hopCount: route.hop_count || 2,
      connectionType: 'multi_hop',
      isHistorical: false
    }));
  } catch (error) {
    console.error('Error fetching mesh routes:', error);
    throw error;
  }
};

/**
 * Fetch historical node connections for the map
 * @param hours Number of hours to look back for historical connections (default: 24)
 * @returns Promise with historical node connections data
 */
export const fetchHistoricalConnections = async (hours: number = 24): Promise<NodeConnection[]> => {
  try {
    // Get self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
      
    // Match the existing API endpoint structure with a timeframe parameter
    const response = await api.get('/historical-neighbors', {
      params: { hours }
    });
    
    // Transform the response to match our frontend data structure
    let connections = response.data.map((connection: any) => ({
      sourceId: parseInt(connection.node_id, 10),
      targetId: parseInt(connection.neighbor_id, 10),
      quality: connection.snr ? Math.min(100, Math.max(0, connection.snr * 10)) : 30, // Lower default quality for historical
      isHistorical: true
    }));
    
    // Filter out connections involving the self/home node
    if (selfNodeId) {
      connections = connections.filter((connection: NodeConnection) => 
        connection.sourceId !== selfNodeId && connection.targetId !== selfNodeId
      );
    }
    
    return connections;
  } catch (error) {
    console.error('Error fetching historical connections:', error);
    throw error;
  }
};

/**
 * Fetch detailed information for a specific node
 * @param nodeId The ID of the node to fetch details for
 * @returns Promise with node details data
 */
export const fetchNodeDetails = async (nodeId: number): Promise<NodeDetails> => {
  try {
    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    // Match the existing API endpoint structure
    // Use the nodeDetailsApi instance with longer timeout
    const response = await nodeDetailsApi.get(`/nodes/${nodeId}`, {
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Transform the response to match our frontend data structure
    return {
      nodeId: response.data.node_id,
      nodeHex: response.data.node_hex,
      longName: response.data.long_name,
      shortName: response.data.short_name,
      hardwareModel: response.data.hardware_model,
      lastSeen: response.data.last_seen,
      batteryLevel: response.data.battery_level || 75,
      voltage: response.data.voltage || 3.8,
      latitude: response.data.latitude,
      longitude: response.data.longitude,
      altitude: response.data.altitude || 0,
      isGateway: response.data.is_gw || false,
    };
  } catch (error) {
    console.error(`Error fetching node details for node ${nodeId}:`, error);
    throw error;
  }
};

/**
 * Fetch telemetry data for a specific node
 * @param nodeId The ID of the node to fetch telemetry for
 * @param hours Number of hours of history to fetch (default: 24)
 * @returns Promise with node telemetry data
 */
export const fetchNodeTelemetry = async (nodeId: number, hours: number = 24): Promise<TelemetryDataPoint[]> => {
  try {
    // Match the existing API endpoint structure
    const response = await api.get(`/telemetry/${nodeId}`, {
      params: { hours }
    });
    
    // Transform the response to match our frontend data structure
    return response.data.map((telemetry: any) => ({
      timestamp: telemetry.timestamp,
      batteryLevel: telemetry.battery_level,
      voltage: telemetry.voltage,
      temperature: telemetry.temperature,
      humidity: telemetry.humidity,
      pressure: telemetry.pressure,
      rssi: telemetry.rssi,
      snr: telemetry.snr,
    }));
  } catch (error) {
    console.error(`Error fetching telemetry for node ${nodeId}:`, error);
    throw error;
  }
};

/**
 * Fetch messages from the network
 * @param limit Maximum number of messages to fetch
 * @param nodeId Optional node ID to filter messages by
 * @returns Promise with messages data
 */
export const fetchMessages = async (limit: number = 10, nodeId?: number): Promise<Message[]> => {
  try {
    // Build query parameters
    const params: any = { limit };
    if (nodeId) {
      params.node_id = nodeId;
    }
    
    // Match the existing API endpoint structure
    const response = await api.get('/messages', { params });
    
    // Transform the response to match our frontend data structure
    return response.data.map((message: any) => ({
      id: message.msg_id,
      senderId: message.from_node,
      senderName: message.from_node_name || `Node-${message.from_node}`,
      receiverId: message.to_node,
      receiverName: message.to_node ? (message.to_node_name || `Node-${message.to_node}`) : 'Broadcast',
      type: message.message_type || 'text',
      content: message.text,
      timestamp: message.timestamp,
      hopCount: message.hop_count || 0,
      isAcknowledged: message.is_acknowledged || false,
      channelId: message.channel_id
    }));
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

/**
 * Fetch recent packets from the network
 * @param limit Maximum number of packets to fetch
 * @returns Promise with packets data
 */
export const fetchPackets = async (limit: number = 10): Promise<Packet[]> => {
  try {
    // Get self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
    
    // Use a shorter timeout for packet fetching to ensure responsiveness
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for packets
    
    try {
      // Connect to the NodeJS backend packet-statistics endpoint
      const response = await api.get('/packet-statistics', { 
        params: { 
          limit: limit * 3, // Request more packets to account for filtering
          order: 'desc',
          include_nodeinfo: true // Request to include nodeinfo data if available
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Transform the response to match our frontend data structure
      // The backend returns { total, limit, offset, data } where data is the array of packets
      const packets = response.data.data.map((packet: any) => {
        // Ensure we have proper node names by prioritizing different sources
        // For fromNode, prioritize: long_name > short_name > hex > numeric ID
        const fromNodeName = 
          packet.from_node_info?.long_name || 
          packet.from_node_info?.short_name || 
          packet.from_node_info?.name || 
          packet.from_node_name || 
          (packet.from_node_hex ? packet.from_node_hex : `Node-${packet.from_node}`);
        
        // For toNode, prioritize: long_name > short_name > hex > numeric ID > "Broadcast"
        const toNodeName = 
          packet.to_node_info?.long_name || 
          packet.to_node_info?.short_name || 
          packet.to_node_info?.name || 
          packet.to_node_name || 
          (packet.to_node_hex ? packet.to_node_hex : 
            (packet.to_node ? `Node-${packet.to_node}` : 'Broadcast'));
        
        return {
          id: packet.id || Math.floor(Math.random() * 10000), // Generate ID if not provided
          timestamp: packet.timestamp,
          fromNode: packet.from_node,
          fromNodeName: fromNodeName || `Node-${packet.from_node}`, // Ensure we always have a name
          fromNodeHex: packet.from_node_hex || (packet.from_node_info?.hex_id ? `!${packet.from_node_info.hex_id}` : undefined),
          toNode: packet.to_node,
          toNodeName: toNodeName || (packet.to_node ? `Node-${packet.to_node}` : 'Broadcast'), // Ensure we always have a name
          toNodeHex: packet.to_node_hex || (packet.to_node_info?.hex_id ? `!${packet.to_node_info.hex_id}` : null),
          type: packet.packet_type || 'unknown',
          isBroadcast: packet.is_broadcast,
          snr: packet.rx_snr,
          rssi: packet.rx_rssi,
          hopLimit: packet.hop_limit,
          isSelfPacket: packet.is_self_packet || false, // Flag for self packets
          // Pass through the nodeinfo data if available
          from_node_info: packet.from_node_info || null,
          to_node_info: packet.to_node_info || null
        };
      });
      
      // Filter out self packets from the radio and packets from self node ID if defined
      const filteredPackets = packets.filter((packet: Packet) => {
        // Filter out self packets
        if (packet.isSelfPacket) {
          return false;
        }
        
        // Filter out packets from self node ID if defined
        // Convert both to strings for comparison to avoid type mismatches
        if (selfNodeId && String(packet.fromNode) === String(selfNodeId)) {
          return false;
        }
        
        return true;
      });
      
      // Limit to the requested number of packets after filtering
      return filteredPackets.slice(0, limit);
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  } catch (error) {
    console.error('Error fetching packets:', error);
    throw error;
  }
};

/**
 * Fetch packet statistics for different time periods
 * @returns Promise with packet statistics data
 */
export const fetchPacketStatistics = async (): Promise<{ lastHour: number; last24Hours: number }> => {
  try {
    // Get self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
    
    try {
      // Try the dedicated endpoint first
      const params: any = {};
      
      // Add exclude_node parameter if self node ID is defined
      if (selfNodeId) {
        params.exclude_node = selfNodeId;
      }
      
      const response = await api.get('/packet-time-statistics', { params });
      
      return {
        lastHour: response.data.lastHour || 0,
        last24Hours: response.data.last24Hours || 0
      };
    } catch (endpointError) {
      console.warn('Packet time statistics endpoint not available, falling back to estimation method:', endpointError);
      
      // Fallback method: use the total count from packet-statistics and estimate
      const response = await api.get('/packet-statistics', {
        params: {
          limit: 1,  // Just need the total count, not the actual data
          order: 'desc'
        }
      });
      
      // Extract total count from response
      const totalPackets = response.data.total || 0;
      
      // Calculate approximate counts based on time periods
      // This is an estimation since we don't have exact time filtering
      const lastHourCount = Math.round(totalPackets * 0.1); // Assume ~10% of packets are from last hour
      const last24HoursCount = Math.round(totalPackets * 0.5); // Assume ~50% of packets are from last 24 hours
      
      // If self node ID is defined, we need to adjust the counts
      const selfNodeAdjustment = selfNodeId ? 0.7 : 1; // Assume about 30% of packets are from self node
      
      return {
        lastHour: Math.round(lastHourCount * selfNodeAdjustment),
        last24Hours: Math.round(last24HoursCount * selfNodeAdjustment)
      };
    }
  } catch (error) {
    console.error('Error fetching packet statistics:', error);
    throw error;
  }
};

/**
 * Helper function to fetch node details individually with progressive loading
 * @param nodeIds Array of node IDs to fetch details for
 * @returns Promise with a map of node IDs to node details
 */
const fetchNodeDetailsIndividually = async (nodeIds: number[]): Promise<Record<number, NodeDetails>> => {
  const detailsMap: Record<number, NodeDetails> = {};
  
  // Process one node at a time to get some data quickly
  for (const nodeId of nodeIds) {
    try {
      // Use a shorter timeout for individual requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await nodeDetailsApi.get(`/nodes/${nodeId}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Transform the response
      detailsMap[nodeId] = {
        nodeId: parseInt(response.data.node_id, 10),
        nodeHex: response.data.node_hex,
        longName: response.data.long_name,
        shortName: response.data.short_name,
        hardwareModel: response.data.hardware_model,
        lastSeen: response.data.last_seen,
        batteryLevel: response.data.battery_level || 75,
        voltage: response.data.voltage || 3.8,
        latitude: response.data.latitude,
        longitude: response.data.longitude,
        altitude: response.data.altitude || 0,
        isGateway: response.data.is_gw || false,
      };
    } catch (error) {
      console.error(`Error fetching details for node ${nodeId}:`, error);
      // Continue with other nodes
    }
    
    // Small delay between requests to avoid overwhelming the API
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return detailsMap;
};

/**
 * Fetch detailed information for multiple nodes in a single request
 * @param nodeIds Array of node IDs to fetch details for
 * @returns Promise with a map of node IDs to node details
 */
export const fetchMultipleNodeDetails = async (nodeIds: number[]): Promise<Record<number, NodeDetails>> => {
  if (!nodeIds.length) return {};
  
  // Start with individual fetching for the first node to get some data quickly
  const firstNodeId = nodeIds[0];
  const initialResult: Record<number, NodeDetails> = {};
  
  try {
    console.log(`Fetching first node ${firstNodeId} to get quick data...`);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    const response = await nodeDetailsApi.get(`/nodes/${firstNodeId}`, {
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Transform the response
    initialResult[firstNodeId] = {
      nodeId: parseInt(response.data.node_id, 10),
      nodeHex: response.data.node_hex,
      longName: response.data.long_name,
      shortName: response.data.short_name,
      hardwareModel: response.data.hardware_model,
      lastSeen: response.data.last_seen,
      batteryLevel: response.data.battery_level || 75,
      voltage: response.data.voltage || 3.8,
      latitude: response.data.latitude,
      longitude: response.data.longitude,
      altitude: response.data.altitude || 0,
      isGateway: response.data.is_gw || false,
    };
    
    // If we got the first node successfully, try the batch for the rest
    const remainingNodeIds = nodeIds.slice(1);
    if (remainingNodeIds.length > 0) {
      try {
        // Use comma-separated list of node IDs
        const idsParam = remainingNodeIds.join(',');
        const batchResponse = await nodeDetailsApi.get(`/nodes/batch`, {
          params: { ids: idsParam }
        });
        
        // Transform the response to a map of nodeId -> NodeDetails
        const detailsMap: Record<number, NodeDetails> = {...initialResult};
        
        // Handle both array and object responses
        const detailsArray = Array.isArray(batchResponse.data) ? 
          batchResponse.data : Object.values(batchResponse.data);
        
        detailsArray.forEach((detail: any) => {
          const nodeId = parseInt(detail.node_id, 10);
          detailsMap[nodeId] = {
            nodeId: nodeId,
            nodeHex: detail.node_hex,
            longName: detail.long_name,
            shortName: detail.short_name,
            hardwareModel: detail.hardware_model,
            lastSeen: detail.last_seen,
            batteryLevel: detail.battery_level || 75,
            voltage: detail.voltage || 3.8,
            latitude: detail.latitude,
            longitude: detail.longitude,
            altitude: detail.altitude || 0,
            isGateway: detail.is_gw || false,
          };
        });
        
        return detailsMap;
      } catch (batchError: any) {
        // If batch fails, we still have the first node
        console.warn('Batch endpoint failed, falling back to individual requests for remaining nodes');
        
        // Fetch remaining nodes individually
        const remainingResults = await fetchNodeDetailsIndividually(remainingNodeIds);
        return {...initialResult, ...remainingResults};
      }
    }
    
    return initialResult;
  } catch (error) {
    console.error(`Error fetching first node ${firstNodeId}:`, error);
    
    // If first node fails, try individual fetching for all nodes
    return fetchNodeDetailsIndividually(nodeIds);
  }
};

/**
 * Fetch complete node data including positions and details in a single request
 * @returns Promise with complete node data
 */
export const fetchCompleteNodeData = async (): Promise<NodePosition[]> => {
  try {
    console.log('Fetching complete node data from /complete-nodes endpoint...');
    const response = await api.get('/complete-nodes');
    console.log(`Complete node data response: ${response.data.length} nodes received`);
    
    // Transform the response to match our frontend data structure
    return response.data.map((node: any) => {
      // Ensure latitude and longitude are valid numbers
      const latitude = parseFloat(node.latitude);
      const longitude = parseFloat(node.longitude);
      
      return {
        nodeId: parseInt(node.node_id, 10),
        latitude: isFinite(latitude) ? latitude : null,
        longitude: isFinite(longitude) ? longitude : null,
        altitude: node.altitude || 0,
        isGateway: node.is_gw === true,
        isOnline: node.is_online === true,
        batteryLevel: node.battery_level || 75,
        lastHeard: node.last_seen || node.timestamp || null, // Use last_seen or timestamp for lastHeard
        // Include node details directly in the position object
        details: {
          nodeId: parseInt(node.node_id, 10),
          nodeHex: node.node_hex,
          longName: node.long_name,
          shortName: node.short_name,
          hardwareModel: node.hardware_model,
          lastSeen: node.last_seen,
          batteryLevel: node.battery_level || 75,
          voltage: node.voltage || 3.8,
          latitude: isFinite(latitude) ? latitude : null,
          longitude: isFinite(longitude) ? longitude : null,
          altitude: node.altitude || 0,
          isGateway: node.is_gw || false,
        }
      };
    });
  } catch (error) {
    console.warn('Complete node data endpoint failed, falling back to batch fetch', error);
    
    // Fallback: Fetch positions first, then get all details in one batch request
    const positions = await fetchNodePositions();
    
    if (positions.length === 0) {
      return positions;
    }
    
    // Get all node IDs
    const nodeIds = positions.map(node => node.nodeId);
    console.log(`Fetching details for ${nodeIds.length} nodes in a single batch...`);
    
    // Fetch all node details in a single batch request
    const detailsMap = await fetchMultipleNodeDetails(nodeIds);
    console.log(`Received details for ${Object.keys(detailsMap).length}/${nodeIds.length} nodes`);
    
    // Merge the details with the positions
    return positions.map(position => ({
      ...position,
      details: detailsMap[position.nodeId] || null
    }));
  }
};

/**
 * Fetch the latest packet from the network
 * @returns Promise with the latest packet data or null if no packets are available
 */
export const fetchLatestPacket = async (): Promise<Packet | null> => {
  try {
    // Get self node ID from environment variable
    const selfNodeId = import.meta.env.VITE_SELF_NODE_ID ? 
      parseInt(import.meta.env.VITE_SELF_NODE_ID as string, 10) : undefined;
    
    // Use a shorter timeout for latest packet fetching to ensure responsiveness
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
    
    try {
      // Connect to the optimized latest-packet endpoint
      const response = await api.get('/latest-packet', { 
        params: { 
          exclude_node: selfNodeId
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // If no packet is available, return null
      if (!response.data) {
        return null;
      }
      
      const packet = response.data;
      
      // Transform the response to match our frontend data structure
      return {
        id: packet.id || Math.floor(Math.random() * 10000), // Generate ID if not provided
        timestamp: packet.timestamp,
        fromNode: packet.from_node,
        fromNodeName: packet.from_node_info?.long_name || 
                     packet.from_node_info?.short_name || 
                     packet.from_node_name || 
                     (packet.from_node_hex ? packet.from_node_hex : `Node-${packet.from_node}`),
        fromNodeHex: packet.from_node_hex || (packet.from_node_info?.hex_id ? `!${packet.from_node_info.hex_id}` : undefined),
        toNode: packet.to_node,
        toNodeName: packet.to_node_info?.long_name || 
                   packet.to_node_info?.short_name || 
                   packet.to_node_name || 
                   (packet.to_node_hex ? packet.to_node_hex : 
                     (packet.to_node ? `Node-${packet.to_node}` : 'Broadcast')),
        toNodeHex: packet.to_node_hex || (packet.to_node_info?.hex_id ? `!${packet.to_node_info.hex_id}` : null),
        type: packet.packet_type || 'unknown',
        isBroadcast: packet.is_broadcast,
        snr: packet.rx_snr,
        rssi: packet.rx_rssi,
        hopLimit: packet.hop_limit,
        isSelfPacket: packet.is_self_packet || false,
        from_node_info: packet.from_node_info || null,
        to_node_info: packet.to_node_info || null
      };
    } catch (endpointError) {
      clearTimeout(timeoutId);
      
      // Log the specific error
      console.warn('Latest packet endpoint error, trying fallback to packets endpoint:', endpointError);
      
      // Fallback to regular packets endpoint if latest-packet fails
      try {
        const fallbackResponse = await api.get('/packet-statistics', {
          params: {
            limit: 1,
            order: 'desc',
            include_nodeinfo: true,
            exclude_node: selfNodeId
          },
          timeout: 3000 // Short timeout for fallback
        });
        
        // Check if we got any packets
        if (!fallbackResponse.data.data || fallbackResponse.data.data.length === 0) {
          return null;
        }
        
        const packet = fallbackResponse.data.data[0];
        
        // Transform the response to match our frontend data structure
        return {
          id: packet.id || Math.floor(Math.random() * 10000),
          timestamp: packet.timestamp,
          fromNode: packet.from_node,
          fromNodeName: packet.from_node_info?.long_name || 
                       packet.from_node_info?.short_name || 
                       packet.from_node_name || 
                       (packet.from_node_hex ? packet.from_node_hex : `Node-${packet.from_node}`),
          fromNodeHex: packet.from_node_hex || (packet.from_node_info?.hex_id ? `!${packet.from_node_info.hex_id}` : undefined),
          toNode: packet.to_node,
          toNodeName: packet.to_node_info?.long_name || 
                     packet.to_node_info?.short_name || 
                     packet.to_node_name || 
                     (packet.to_node_hex ? packet.to_node_hex : 
                       (packet.to_node ? `Node-${packet.to_node}` : 'Broadcast')),
          toNodeHex: packet.to_node_hex || (packet.to_node_info?.hex_id ? `!${packet.to_node_info.hex_id}` : null),
          type: packet.packet_type || 'unknown',
          isBroadcast: packet.is_broadcast,
          snr: packet.rx_snr,
          rssi: packet.rx_rssi,
          hopLimit: packet.hop_limit,
          isSelfPacket: packet.is_self_packet || false,
          from_node_info: packet.from_node_info || null,
          to_node_info: packet.to_node_info || null
        };
      } catch (fallbackError) {
        console.error('Both latest packet endpoints failed:', fallbackError);
        throw fallbackError;
      }
    }
  } catch (error) {
    console.error('Error fetching latest packet:', error);
    return null;
  }
};

export default api;